import { SoundSpriteCache, fetchAudioBytes } from './sound-buffer-cache';
import type { Atlas } from '../assets/atlas';
import { ASSET_PREFIX, sharedAtlas } from '../assets/asset-store';
import {
  cueVolume,
  pickVariant,
  soundClipName,
  type SoundCategory,
  type SoundCue,
  type SoundListener,
} from './sounds';

export interface PreferenceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface SoundStoreOptions {
  readonly atlas?: Atlas | undefined;
  readonly prefix?: string | undefined;
  readonly storage?: PreferenceStorage | null | undefined;
  readonly createContext?: (() => AudioContext) | undefined;
  readonly fetchAudio?: ((url: string) => Promise<ArrayBuffer>) | undefined;
  readonly random?: (() => number) | undefined;
}

const ENABLED_KEY = 'soundOn';
const VOLUME_KEY = 'soundVolume';
const EFFECTS_KEY = 'soundEffectsVolume';
const MUSIC_KEY = 'musicVolume';

function defaultStorage(): PreferenceStorage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export class SoundStore {
  private readonly atlas: Atlas;
  private readonly prefix: string;
  private readonly storage: PreferenceStorage | null;
  private readonly createContext: () => AudioContext;
  private readonly fetchAudio: (url: string) => Promise<ArrayBuffer>;
  private readonly random: () => number;

  private ctx: AudioContext | null = null;
  private cache: SoundSpriteCache | null = null;
  private readonly listeners = new Set<() => void>();

  played = 0;

  private enabledValue: boolean;
  private volumeValue: number;
  private effectsValue: number;
  private musicValue: number;

  constructor(options: SoundStoreOptions = {}) {
    this.atlas = options.atlas ?? sharedAtlas;
    this.prefix = options.prefix ?? ASSET_PREFIX;
    this.storage = options.storage === undefined ? defaultStorage() : options.storage;
    this.createContext = options.createContext ?? (() => new AudioContext());
    this.fetchAudio = options.fetchAudio ?? fetchAudioBytes;
    this.random = options.random ?? Math.random;

    this.enabledValue = this.read(ENABLED_KEY) !== '0';
    this.volumeValue = readVolume(this.read(VOLUME_KEY));
    this.effectsValue = readVolume(this.read(EFFECTS_KEY));
    this.musicValue = readVolume(this.read(MUSIC_KEY));
  }

  get enabled(): boolean {
    return this.enabledValue;
  }

  set enabled(value: boolean) {
    this.enabledValue = value;
    this.write(ENABLED_KEY, value ? '1' : '0');
    this.notify();
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  get volume(): number {
    return this.volumeValue;
  }

  set volume(value: number) {
    this.volumeValue = clamp01(value);
    this.write(VOLUME_KEY, String(this.volumeValue));
    this.notify();
  }

  get effectsVolume(): number {
    return this.effectsValue;
  }

  set effectsVolume(value: number) {
    this.effectsValue = clamp01(value);
    this.write(EFFECTS_KEY, String(this.effectsValue));
    this.notify();
  }

  get musicVolume(): number {
    return this.musicValue;
  }

  set musicVolume(value: number) {
    this.musicValue = clamp01(value);
    this.write(MUSIC_KEY, String(this.musicValue));
    this.notify();
  }

  get musicGain(): number {
    return this.enabledValue ? this.volumeValue * this.musicValue : 0;
  }

  get started(): boolean {
    return this.ctx !== null;
  }

  /** Must be reached from a user gesture — anything else is refused by the browser and is not worth reporting. */
  resume(): void {
    void this.atlas.load().catch(() => undefined);
    let appeared = false;
    if (this.ctx === null) {
      try {
        this.ctx = this.createContext();
      } catch {
        return;
      }
      const context = this.ctx;
      this.cache = new SoundSpriteCache({
        atlas: this.atlas,
        prefix: this.prefix,
        fetchAudio: this.fetchAudio,
        decode: (data) => context.decodeAudioData(data),
      });
      this.cache.preload();
      appeared = true;
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume().catch(() => undefined);
    }
    if (appeared) this.notify();
  }

  playCues(cues: SoundCue[], listener: SoundListener): void {
    if (cues.length === 0) return;
    if (this.enabledValue && this.ctx !== null) {
      for (const cue of cues) this.play(cue.category, cueVolume(cue, listener));
    }
    cues.length = 0;
  }

  play(category: SoundCategory, volume: number): void {
    const context = this.ctx;
    if (context === null || !this.enabledValue) return;

    const gain = clamp01(volume) * this.volumeValue * this.effectsValue;
    if (gain <= 0) return;

    const clip =
      this.cache?.get(soundClipName(category, pickVariant(category, this.random()))) ?? null;
    if (clip === null) return;

    const source = context.createBufferSource();
    source.buffer = clip.buffer;
    const gainNode = context.createGain();
    gainNode.gain.value = gain;
    source.connect(gainNode);
    gainNode.connect(context.destination);
    source.start(0, clip.offset, clip.duration);
    this.played += 1;
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  private read(key: string): string | null {
    try {
      return this.storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      this.storage?.setItem(key, value);
    } catch {
      // must not break the toggle the player just clicked
    }
  }
}

/** `Number(null)` is 0, a finite non-negative number — coercing before checking for "no preference" would read as silent and mute new players with nothing to explain it. */
function readVolume(stored: string | null): number {
  if (stored === null || stored === '') return 1;
  const parsed = Number(stored);
  return Number.isFinite(parsed) && parsed >= 0 ? clamp01(parsed) : 1;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}
